import { filter } from 'lodash';
import moment from 'moment';
import React, { useMemo } from 'react';
import {
  buildOptionsFromData,
  ErrorHandler,
  ExternalLink,
  FilteredTable,
  Gutter,
  InfoTooltip,
  Link,
  MultiSelectTableFilter,
  RenderTableColumn,
  StatePill,
  StatusBadge,
  Text,
  TextFilterSearch,
  TextSearchTableFilter,
} from '@common/components';

import { ApplicationActionsDropdown } from '@applicant-flow/components';
import {
  MISSION_CONTROL_PAGINATED_STAFFING_REQUEST_APPLICATIONS_QUERY,
  MissionControlStaffingRequestApplicationType,
} from '@applicant-flow/graphql';
import { useApplicationAction, useOfferActions } from '@applicant-flow/hooks';
import { RolesQuery, RolesResponse } from '@clinician-profile/graphql';
import {
  MissionControlPaginatedStaffingRequestApplicationsQueryVariables,
  OrganizationBusinessesQueryCgResponse,
  StaffingRequestApplicationFilterInput,
  StaffingRequestApplicationState,
  SupplierNamesQueryCgResponse,
  WorksClinicalUnitsQueryCgResponse,
} from '@common/graphql';
import { useLazyQuery, usePaginationHelper, useQuery } from '@common/hooks';
import { buildDialogUrl } from '@common/services';
import {
  ORGANIZATION_BUSINESSES_QUERY,
  SUPPLIER_NAMES_QUERY,
  WORKS_CLINICAL_UNITS_QUERY,
} from '@institutions/graphql';
import { CLEANUP_WHEN_EMPTY, nonFalse } from '@pay-bill/services';
import { toUSCompactDateFormat } from 'works/legacy/lib/date-helper';
import { Spacing } from 'works/legacy/lib/style-helper';

type TableKind = 'InScreening' | 'OffersPending';

type Props = {
  tableKind: TableKind;
  defaultStateFilters: StaffingRequestApplicationState[];
};

const refetchQueries = [
  'MissionControlPaginatedStaffingRequestApplications',
  'MissionControlStaffingRequestCounters',
];

export function MissionControlStaffingTableCandidatesBase<
  T extends object = MissionControlStaffingRequestApplicationType,
>({ tableKind, defaultStateFilters }: Props) {
  const {
    filters,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: `MissionControlStaffingCandidatesTable-${tableKind}`,
    defaultPaginationData: {
      defaultPageSize: 50,
      limit: 50,
      offset: 0,
      orderDirection: 'DESC',
      orderField: 'SECONDS_UNTIL_START_DATE',
    },
    columnsDictionary: {
      name: 'NAME',
      role: 'ROLE',
      clinicalUnit: 'CLINICAL_UNIT',
      organizationBusiness: 'FACILITY',
      state: 'STATUS',
      supplier: 'SUPPLIER',
      startDate: 'START',
      code: 'JOB_ID',
      secondsUntilStartDate: 'SECONDS_UNTIL_START_DATE',
      ageOfOffer: 'AGE_OF_OFFER_IN_SECONDS',
      openPositions: 'OPEN_POSITIONS_COUNT',
    },
  });

  const [fetchSupplier, { data: supplierData, error: supplierError }] =
    useLazyQuery(SUPPLIER_NAMES_QUERY);

  const [fetchOrgBusiness, { data: orgBusinessData, error: orgBusinessError }] =
    useLazyQuery(ORGANIZATION_BUSINESSES_QUERY);

  const [fetchRoles, { data: rolesData, error: rolesError }] =
    useLazyQuery(RolesQuery);

  const [
    fetchClinicalUnits,
    { data: clinicalUnitData, error: clinicalUnitError },
  ] = useLazyQuery(WORKS_CLINICAL_UNITS_QUERY);

  const {
    data,
    error,
    loading: loadingApplications,
  } = useQuery(MISSION_CONTROL_PAGINATED_STAFFING_REQUEST_APPLICATIONS_QUERY, {
    variables: {
      ...paginationVariables,
      orderDirection:
        paginationVariables.orderDirection as MissionControlPaginatedStaffingRequestApplicationsQueryVariables['orderDirection'],
      orderField:
        paginationVariables.orderField as MissionControlPaginatedStaffingRequestApplicationsQueryVariables['orderField'],
      filters: {
        ...paginationVariables.filters,
      },
      states: defaultStateFilters,
    },
    onCompleted() {
      fetchSupplier();
      fetchOrgBusiness();
      fetchClinicalUnits();
      fetchRoles();
    },
  });

  const page = data?.missionControlPaginatedStaffingRequestApplications;
  const dataSource = useMemo(
    () => page?.staffingRequestApplications || [],
    [page]
  );
  const totalCount = page?.totalCount ?? 0;

  const { shortlistForReview, loading: loadingSFRMutation } =
    useApplicationAction({
      refetchQueries,
    });

  const { retractOffer, loading: loadingROMutation } = useOfferActions({
    refetchQueries,
  });

  const columns = useMemo(() => {
    return getColumns({
      filters: filters as StaffingRequestApplicationFilterInput,
      supplierData,
      orgBusinessData,
      clinicalUnitData,
      rolesData,
      tableKind,
      shortlistForReview,
      retractOffer,
    }) as unknown as RenderTableColumn<T>[];
  }, [
    filters,
    supplierData,
    orgBusinessData,
    clinicalUnitData,
    rolesData,
    tableKind,
    shortlistForReview,
    retractOffer,
  ]);

  const loading =
    loadingApplications || loadingSFRMutation || loadingROMutation;

  const anyError =
    error ||
    supplierError ||
    orgBusinessError ||
    clinicalUnitError ||
    rolesError;

  const filtersDataReady =
    !!supplierData && !!orgBusinessData && !!clinicalUnitData && !!rolesData;

  if (anyError) {
    return <ErrorHandler error={anyError} log="MissionControlStaffingTable" />;
  }

  return (
    <>
      <FilteredTable
        rowKey="id"
        className="mission-control-staffing-table"
        columns={columns}
        dataSource={dataSource}
        pagination={{ ...paginationVariables, total: totalCount }}
        loading={loading}
        onChange={tableChangeHandler}
        onFilterChange={filterChangeHandler}
        filters={filters}
        localStorageFilterKey={localStorageFilterKey}
        filtersDataReady={filtersDataReady}
      />
      {!loading && (
        <Gutter size="sm" direction="vertical">
          Showing {dataSource.length}.{' '}
          <ExternalLink url="/works/applications">View All</ExternalLink>.
        </Gutter>
      )}
    </>
  );
}

const getColumns = ({
  filters,
  supplierData,
  orgBusinessData,
  clinicalUnitData,
  rolesData,
  tableKind,
  shortlistForReview,
  retractOffer,
}: {
  filters: StaffingRequestApplicationFilterInput;
  supplierData?: SupplierNamesQueryCgResponse;
  orgBusinessData?: OrganizationBusinessesQueryCgResponse;
  clinicalUnitData?: WorksClinicalUnitsQueryCgResponse;
  rolesData?: RolesResponse;
  tableKind: TableKind;
  shortlistForReview: (applicationId?: string | null) => void;
  retractOffer: (offerId?: string | null) => void;
}) => {
  const isInScreening = tableKind === 'InScreening';
  const isOffersPending = tableKind === 'OffersPending';

  return filter<
    RenderTableColumn<MissionControlStaffingRequestApplicationType> | false,
    RenderTableColumn<MissionControlStaffingRequestApplicationType>
  >(
    [
      {
        title: 'Name',
        key: 'name',
        className: 'candidate-name-col sentry-mask',
        dataIndex: ['worksProfile', 'fullName'],
        sorter: true,
        filtered: !!filters.name?.length,
        filteredValue: filters.name || CLEANUP_WHEN_EMPTY,
        filterFormat: 'id',
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              autoFocus
              id="filter-by-candidate-name-input"
              placeHolder="Filter by name"
              onSearch={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </section>
        ),
        render: (
          _: unknown,
          application: MissionControlStaffingRequestApplicationType
        ) => {
          const nameLink =
            isOffersPending && application.activeOffer?.id
              ? buildDialogUrl('offer-preview-drawer', {
                  props: { offerId: application.activeOffer?.id },
                })
              : `/works/applications/${application.id}/summary`;

          return (
            <Link to={nameLink} target={isInScreening ? '_blank' : undefined}>
              {application.worksProfile.fullName}
            </Link>
          );
        },
      },
      {
        title: 'Job ID',
        dataIndex: ['staffingRequest', 'code'],
        key: 'code',
        filtered: !!filters.code || false,
        filteredValue: filters.code || null,
        filterDropdown: TextSearchTableFilter,
        sorter: true,
      },
      {
        title: 'Supplier',
        key: 'supplier',
        dataIndex: ['supplier', 'name'],
        sorter: true,
        filtered: !!filters.supplier?.length,
        filteredValue: filters.supplier || CLEANUP_WHEN_EMPTY,
        filterDropdown: MultiSelectTableFilter,
        filters: supplierData && buildOptionsFromData(supplierData.suppliers),
      },
      {
        title: 'Role',
        key: 'role',
        dataIndex: ['staffingRequest', 'role', 'abbreviation'],
        sorter: true,
        filtered: !!filters.role,
        filteredValue: filters.role || null,
        filters:
          rolesData?.roles &&
          buildOptionsFromData(
            rolesData.roles,
            (role: { id: string; abbreviation: string }) => ({
              text: role.abbreviation,
              value: role.id,
            })
          ),
        filterDropdown: MultiSelectTableFilter,
      },
      {
        title: isInScreening ? (
          'Start Date'
        ) : (
          <>
            Start Date{' '}
            <InfoTooltip
              leftOffset={0}
              title="The offer start date can be different than original start date."
            />
          </>
        ),
        key: 'startDate',
        dataIndex: 'staffingRequest',
        sorter: true,
        render: (
          staffingRequest: MissionControlStaffingRequestApplicationType['staffingRequest']
        ) => toUSCompactDateFormat(staffingRequest.startDate),
      },
      {
        title: 'Lead Time',
        dataIndex: 'secondsUntilStartDate',
        key: 'secondsUntilStartDate',
        sorter: true,
        render: (secondsUntilStartDate, record) => (
          <StatusBadge
            value={moment
              .duration(secondsUntilStartDate, 'seconds')
              .humanize(true)}
            type={record.timeUntilStartDateType}
          />
        ),
      },
      isOffersPending && {
        title: 'Age of Offer',
        dataIndex: 'ageOfOfferInSeconds',
        key: 'ageOfOffer',
        sorter: true,
        render: (ageOfOffer, record) => (
          <StatusBadge
            value={moment.duration(ageOfOffer, 'seconds').humanize()}
            type={record.ageOfOfferType}
          />
        ),
      },
      {
        title: 'Facility',
        dataIndex: ['staffingRequest', 'organizationBusiness', 'name'],
        key: 'organizationBusiness',
        sorter: true,
        filtered: !!filters.organizationBusiness,
        filteredValue: filters.organizationBusiness || null,
        filters: buildOptionsFromData(
          orgBusinessData?.organizationBusinesses || [],
          (organizationBusiness: { id: string; name: string }) => ({
            value: organizationBusiness.id,
            text: organizationBusiness.name,
          })
        ),
        filterDropdown: MultiSelectTableFilter,
      },
      {
        title: 'Unit',
        dataIndex: [
          'staffingRequest',
          'organizationUnit',
          'clinicalUnit',
          'displayName',
        ],
        key: 'clinicalUnit',
        sorter: true,
        filtered: !!filters.clinicalUnit,
        filteredValue: filters.clinicalUnit || null,
        filters:
          clinicalUnitData &&
          buildOptionsFromData(
            clinicalUnitData.worksClinicalUnits,
            (worksClinicalUnit: { id: string; displayName: string }) => ({
              value: worksClinicalUnit.id,
              text: worksClinicalUnit.displayName,
            })
          ),
        filterDropdown: MultiSelectTableFilter,
      },
      isInScreening && {
        title: 'Open Positions',
        dataIndex: ['staffingRequest', 'openStaffingRequestPositionsCount'],
        key: 'openPositions',
        sorter: true,
      },
      {
        title: 'Candidate Status',
        key: 'state',
        dataIndex: 'state',
        sorter: true,
        render: (status) => (
          <StatePill color="steel-lighter">
            <Text transform="capitalize">{status.replace(/_/g, ' ')}</Text>
          </StatePill>
        ),
      },
      {
        title: 'Action',
        render: (
          _: unknown,
          application: MissionControlStaffingRequestApplicationType
        ) => (
          <ApplicationActionsDropdown
            application={application}
            shortlistForReview={shortlistForReview}
            retractOffer={retractOffer}
          />
        ),
      },
    ],
    nonFalse
  );
};
